<script setup>
import { defineProps, computed, reactive } from 'vue';
import Message from './Message.vue';
import DateSeparator from 'shared/components/DateSeparator.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';
import { formatUnixDate } from 'shared/helpers/DateHelper';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  return useCamelCase(props.messages, { deep: true });
});

// Helper to get message date string for grouping and display
const getMessageDate = (message) => {
  const timestamp = message.createdAt || message.created_at;
  if (!timestamp) return '';
  // Handle both Unix timestamp (seconds) and milliseconds
  const unixTimestamp = typeof timestamp === 'string' 
    ? new Date(timestamp).getTime() / 1000 
    : timestamp > 1000000000000 
      ? timestamp / 1000 
      : timestamp;
  // Return formatted date string (e.g., "Dec 03, 2024") which DateSeparator can parse
  return formatUnixDate(unixTimestamp, 'MMM dd, yyyy');
};

const groupedMessagesWithDates = computed(() => {
  if (!allMessages.value || allMessages.value.length === 0) {
    return [];
  }

  const result = [];
  let previousDate = '';

  allMessages.value.forEach((message, index) => {
    const currentDate = getMessageDate(message);
    
    // Add date separator if date changed and it's not the first message
    if (currentDate && currentDate !== previousDate && previousDate !== '') {
      result.push({ 
        type: 'date-separator', 
        date: currentDate, 
        id: `date-${currentDate}-${index}` 
      });
    }
    
    // Add the message
    result.push({ type: 'message', ...message, originalIndex: index });
    
    previousDate = currentDate;
  });

  return result;
});

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message in the grouped list
 * @param {Array} searchList - Array of messages/date separators to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  // Skip date separators to find the next message
  let nextIndex = index + 1;
  while (nextIndex < searchList.length && searchList[nextIndex].type === 'date-separator') {
    nextIndex++;
  }
  
  if (nextIndex >= searchList.length) return false;
  
  const next = searchList[nextIndex];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  const currentTimestamp = current.createdAt || current.created_at;
  const nextTimestamp = next.createdAt || next.created_at;
  if (!currentTimestamp || !nextTimestamp) return false;
  
  // Handle both Unix timestamp (seconds) and milliseconds
  const currentUnix = typeof currentTimestamp === 'string' 
    ? new Date(currentTimestamp).getTime() / 1000 
    : currentTimestamp > 1000000000000 
      ? currentTimestamp / 1000 
      : currentTimestamp;
  const nextUnix = typeof nextTimestamp === 'string' 
    ? new Date(nextTimestamp).getTime() / 1000 
    : nextTimestamp > 1000000000000 
      ? nextTimestamp / 1000 
      : nextTimestamp;
  
  return Math.floor(nextUnix / 60) === Math.floor(currentUnix / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul class="px-4 bg-n-background">
    <slot name="beforeAll" />
    <template v-for="(item, index) in groupedMessagesWithDates" :key="item.type === 'date-separator' ? item.id : item.id">
      <DateSeparator
        v-if="item.type === 'date-separator'"
        :date="item.date"
      />
      <template v-else>
        <slot
          v-if="firstUnreadId && item.id === firstUnreadId"
          name="unreadBadge"
        />
        <Message
          v-bind="item"
          :is-email-inbox="isAnEmailChannel"
          :in-reply-to="getInReplyToMessage(item)"
          :group-with-next="shouldGroupWithNext(index, groupedMessagesWithDates)"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :current-user-id="currentUserId"
          data-clarity-mask="True"
          @retry="emit('retry', item)"
        />
      </template>
    </template>
    <slot name="after" />
  </ul>
</template>
